import { useEffect, useState } from "react"
import Correct from "./Correct.js"
import { Incorrect } from "./Incorrect.js"

const Category = ({
  category,
  openCategoryModal,
  selectedAnswer,
  setSelectedAnswer,
  incorrectAnswer,
  setIncorrectAnswer,
  setHasBeenAnswered,
  categoryId,
  setScore,
  selectedClueIndex,
  idx,
  setCluesAnswered,
  selectedOption,
  setSelectedOption,
  activeCert,
}) => {
  const [shuffledOptions, setShuffledOptions] = useState([])
  const [categories1, setCategories] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [validationError, setValidationError] = useState("")

  const shuffleArray = (array) => {
    const shuffledArray = [...array]
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      const temp = shuffledArray[i]
      shuffledArray[i] = shuffledArray[j]
      shuffledArray[j] = temp
    }

    return shuffledArray
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setLoadError(null)
      try {
        const endpoint =
          activeCert === "admin2" ? "/api/clues/admin2" :
          activeCert === "app-builder" ? "/api/clues/app-builder" :
          activeCert === "business-analyst" ? "/api/clues/business-analyst" :
          activeCert === "developer1" ? "/api/clues/developer1" :
          "/api/clues"
        const response = await fetch(endpoint)

        if (!response.ok) {
          throw new Error("Failed to load questions")
        }

        const data = await response.json()
        setCategories(data)

        // Check if selectedClueIndex is valid and data is available
        if (selectedClueIndex >= 0 && selectedClueIndex < data.length) {
          setShuffledOptions(
            shuffleArray([
              data[selectedClueIndex].option1,
              data[selectedClueIndex].option2,
              data[selectedClueIndex].option3,
              data[selectedClueIndex].option4,
            ]),
          )
        }
      } catch (error) {
        console.error("error", error)
        setLoadError(error.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [selectedClueIndex, categoryId, activeCert])

  const handleSubmit = (categoryId, event) => {
    event.preventDefault()

    const answer = categories1[selectedClueIndex].answer
    const requiredCount = answer.length

    if (selectedOption.length !== requiredCount) {
      setValidationError(
        `Please select ${requiredCount} answer${requiredCount > 1 ? "s" : ""}.`
      )
      return
    }

    setValidationError("")

    const normalize = (s) => s.replace(/\s+/g, " ").trim()
    const isAnswerCorrect =
      selectedOption.length === answer.length &&
      selectedOption.every((opt) => {
        const normOpt = normalize(opt)
        return answer.some((a) => {
          const normA = normalize(a)
          return normOpt === normA || normOpt.startsWith(normA)
        })
      })

    if (isAnswerCorrect) {
      setSelectedAnswer(true)
      setScore((prev) => prev + categories1[selectedClueIndex].points)
    } else {
      setIncorrectAnswer(true)
    }

    setCluesAnswered((prev) => prev + 1)
    setHasBeenAnswered((prev) => {
      const newHasBeenAnswered = [...prev]
      newHasBeenAnswered[idx] = {
        answered: true,
        id: idx,
      }
      return newHasBeenAnswered
    })
  }

  if (category) {
    document.body.classList.add("active-modal")
  } else {
    document.body.classList.remove("active-modal")
  }

  return (
    <>
      {category && (isLoading || loadError || categories1.length === 0 || categories1[selectedClueIndex]) ? (
        <>
          <div className="categoryOverlay"></div>

          <div
            className="categoryModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="question-heading"
          >
            <div className="closeButtonContainer">
              <button
                className="modalButton"
                onClick={openCategoryModal}
                aria-label="Close modal"
              >
                X
              </button>
            </div>

            {isLoading ? (
              <div className="loadingSpinner">
                <div className="spinner"></div>
                <p>Loading question...</p>
              </div>
            ) : loadError ? (
              <div className="loadingSpinner">
                <p>Error: {loadError}</p>
                <button className="submitButton" onClick={() => window.location.reload()}>
                  Retry
                </button>
              </div>
            ) : categories1.length === 0 ? (
              <div className="loadingSpinner">
                <p>Questions coming soon!</p>
                <p style={{ fontSize: "1rem", marginTop: "1rem", color: "#666" }}>
                  We're working on adding questions for this certification.
                </p>
              </div>
            ) : categories1[selectedClueIndex] ? (
            <div>
              <div className="question">
                <p className="questionP">
                  {categories1[selectedClueIndex].question}
                </p>
              </div>

              <div className="category" data-testid="category">
                <form onSubmit={(e) => handleSubmit(categoryId, e)}>
                  {shuffledOptions.map((option, index) => (
                    <div className="formContainer" key={index}>
                      <input
                        className="radioBtn"
                        type={
                          categories1[selectedClueIndex].answer.length > 1
                            ? "checkbox"
                            : "radio"
                        }
                        id={`option${index + 1}`}
                        name="picklist"
                        value={option}
                        onChange={(e) => {
                          setValidationError("")
                          const optionValue = e.target.value
                          const isChecked = e.target.checked

                          setSelectedOption((prevOptions) => {
                            if (isChecked && e.target.type === "radio") {
                              // If a new radio option is checked, replace the previous selectedOption
                              return [optionValue]
                            } else if (
                              isChecked &&
                              e.target.type === "checkbox"
                            ) {
                              // If a checkbox is checked, add the new option to selectedOption
                              return [...prevOptions, optionValue]
                            } else {
                              // For other cases, return the current selectedOption
                              return prevOptions.filter(
                                (option) => option !== optionValue,
                              )
                            }
                          })
                        }}
                      />
                      <label
                        htmlFor={`option${index + 1}`}
                        className="categoryLabel"
                      >
                        {option}
                      </label>
                      <br />
                    </div>
                  ))}

                  {validationError && (
                    <p className="validationError">{validationError}</p>
                  )}
                  <div className="submitContainer">
                    <button type="submit" className="submitButton">
                      Submit Answer
                    </button>
                  </div>
                </form>
              </div>
            </div>
            ) : null}
          </div>
        </>
      ) : null}
      <div>
        {!selectedAnswer && incorrectAnswer ? (
          <Incorrect
            openCategoryModal={openCategoryModal}
            categoryId={categoryId}
            selectedClueIndex={selectedClueIndex}
            selectedOption={selectedOption}
            categories1={categories1}
            shuffledOptions={shuffledOptions}
          />
        ) : selectedAnswer && !incorrectAnswer ? (
          <Correct
            openCategoryModal={openCategoryModal}
            categoryId={categoryId}
            selectedClueIndex={selectedClueIndex}
            selectedOption={selectedOption}
            categories1={categories1}
            shuffledOptions={shuffledOptions}
          />
        ) : null}
      </div>
    </>
  )
}

export default Category
